import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Download, Search, Sparkles, Star, Clock, Users } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface TrainingWin {
  id: string;
  participant_name: string;
  email: string | null;
  training_type: string;
  training_day: number;
  win_text: string;
  key_takeaway: string | null;
  review_status: 'new' | 'reviewed' | 'featured' | 'archived';
  admin_note: string | null;
  submitted_at: string;
  reviewed_at: string | null;
}

const TRAINING_LABELS: Record<string, string> = {
  '7-day-new-model-nlp': '7-Day New Model of NLP',
  'nlp-os': 'NLP OS',
  'classical-nlp-practitioner': 'Classical NLP Practitioner',
  'new-model-enrolment-method': 'New Model of NLP Enrolment Method Training',
};

const STATUS_LABELS: Record<TrainingWin['review_status'], string> = {
  new: 'New',
  reviewed: 'Reviewed',
  featured: 'Featured',
  archived: 'Archived',
};

export function WinsReviews() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wins, setWins] = useState<TrainingWin[]>([]);
  const [selectedWin, setSelectedWin] = useState<TrainingWin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [trainingFilter, setTrainingFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadWins();
  }, [user, navigate]);

  const loadWins = async () => {
    if (!user?.id) return;
    const isAdmin = await isUserAdmin(user.id);
    if (!isAdmin) {
      navigate('/login');
      return;
    }

    const { data, error: loadError } = await supabase
      .from('training_wins')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (loadError) {
      setError('We could not load the wins right now.');
    } else {
      setWins((data || []) as TrainingWin[]);
    }
    setIsLoading(false);
  };

  const filteredWins = useMemo(() => wins.filter((win) => {
    const matchesSearch = `${win.participant_name} ${win.win_text}`
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || win.review_status === statusFilter;
    const matchesTraining = trainingFilter === 'all' || win.training_type === trainingFilter;
    const matchesDay = dayFilter === 'all' || String(win.training_day) === dayFilter;
    return matchesSearch && matchesStatus && matchesTraining && matchesDay;
  }), [wins, search, statusFilter, trainingFilter, dayFilter]);

  const counts = {
    total: wins.length,
    new: wins.filter((win) => win.review_status === 'new').length,
    reviewed: wins.filter((win) => win.review_status === 'reviewed').length,
    featured: wins.filter((win) => win.review_status === 'featured').length,
  };

  const updateWin = async (updates: Partial<TrainingWin>) => {
    if (!selectedWin) return;
    setIsSaving(true);
    setError(null);

    const { data, error: updateError } = await supabase
      .from('training_wins')
      .update({ ...updates, reviewed_at: new Date().toISOString() })
      .eq('id', selectedWin.id)
      .select()
      .maybeSingle();

    if (updateError || !data) {
      setError('We could not save those review changes.');
    } else {
      const updated = data as TrainingWin;
      setWins((current) => current.map((win) => win.id === updated.id ? updated : win));
      setSelectedWin(updated);
    }
    setIsSaving(false);
  };

  const downloadCSV = () => {
    const rows = [['Name', 'Training', 'Day', 'Win', 'Key Takeaway', 'Status', 'Submitted At']];
    filteredWins.forEach((win) => rows.push([
      win.participant_name,
      TRAINING_LABELS[win.training_type] || win.training_type,
      `Day ${win.training_day}`,
      win.win_text,
      win.key_takeaway || '',
      STATUS_LABELS[win.review_status],
      new Date(win.submitted_at).toLocaleString(),
    ]));
    const csv = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'training-wins.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <LoadingSpinner message="Loading wins and reviews..." />;

  return (
    <div className="min-h-screen bg-brand-5">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="p-2 hover:bg-gray-10 rounded-lg transition-colors text-gray-50">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="p-2.5 bg-success-50 rounded-xl shadow-soft">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-heading-xs text-gray-80">Wins / Reviews</h1>
              <p className="text-text-sm text-gray-40">Daily training reflections from participants</p>
            </div>
          </div>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-2 px-4 py-2.5 bg-success-50 hover:bg-success-70 text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Wins', value: counts.total, icon: Users, color: 'bg-brand-10 text-brand-60' },
            { label: 'New', value: counts.new, icon: Clock, color: 'bg-orange-10 text-orange-50' },
            { label: 'Reviewed', value: counts.reviewed, icon: CheckCircle, color: 'bg-teal-10 text-teal-50' },
            { label: 'Featured', value: counts.featured, icon: Star, color: 'bg-success-10 text-success-70' },
          ].map((stat) => (
            <div key={stat.label} className="freud-card-elevated p-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div className="text-heading-sm text-gray-80">{stat.value}</div>
              <div className="text-text-xs text-gray-40">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-40" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by participant or win..."
              className="w-full pl-10 pr-4 py-3 border border-gray-20 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-success-40"
            />
          </div>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-60">
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="featured">Featured</option>
            <option value="archived">Archived</option>
          </select>
          <select value={trainingFilter} onChange={(event) => setTrainingFilter(event.target.value)} className="px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-60">
            <option value="all">All training programs</option>
            <option value="7-day-new-model-nlp">7-Day New Model of NLP</option>
            <option value="nlp-os">NLP OS</option>
            <option value="classical-nlp-practitioner">Classical NLP Practitioner</option>
            <option value="new-model-enrolment-method">Enrolment Method Training</option>
          </select>
          <select value={dayFilter} onChange={(event) => setDayFilter(event.target.value)} className="px-4 py-3 border border-gray-20 rounded-xl text-sm bg-white text-gray-60">
            <option value="all">All training days</option>
            {[1, 2, 3, 4, 5, 6, 7].map((day) => <option key={day} value={day}>Day {day}</option>)}
          </select>
        </div>

        <div className="grid lg:grid-cols-[1fr_420px] gap-6 items-start">
          <div className="freud-card-elevated overflow-hidden">
            {filteredWins.length === 0 ? (
              <div className="p-16 text-center text-gray-40 text-sm">{search || statusFilter !== 'all' || trainingFilter !== 'all' || dayFilter !== 'all' ? 'No wins match your filters.' : 'No wins have been submitted yet.'}</div>
            ) : (
              <div className="divide-y divide-gray-20">
                {filteredWins.map((win, index) => (
                  <motion.button
                    key={win.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    onClick={() => setSelectedWin(win)}
                    className={`w-full text-left p-5 hover:bg-gray-5 transition-colors ${selectedWin?.id === win.id ? 'bg-success-5 border-l-4 border-success-40' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <div className="font-semibold text-gray-80">{win.participant_name}</div>
                      </div>
                      <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${win.review_status === 'new' ? 'bg-orange-10 text-orange-70' : win.review_status === 'featured' ? 'bg-success-10 text-success-70' : win.review_status === 'archived' ? 'bg-gray-10 text-gray-50' : 'bg-teal-10 text-teal-50'}`}>
                        {STATUS_LABELS[win.review_status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-40 mb-2">
                      <span>{TRAINING_LABELS[win.training_type] || win.training_type}</span>
                      <span>•</span>
                      <span>Day {win.training_day}</span>
                      <span>•</span>
                      <span>{new Date(win.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-gray-60 line-clamp-2">{win.win_text}</p>
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          <div className="freud-card-elevated p-6 lg:sticky lg:top-6">
            {!selectedWin ? (
              <div className="py-16 text-center text-gray-40">
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-success-40" />
                <p className="text-sm">Select a win to read and review it.</p>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <h2 className="text-text-xl text-gray-80">{selectedWin.participant_name}</h2>
                  </div>
                  <span className="text-xs text-gray-40 whitespace-nowrap">Day {selectedWin.training_day}</span>
                </div>
                <div className="px-4 py-4 rounded-xl bg-success-5 border border-success-10 mb-5">
                  <div className="flex items-center gap-2 mb-2 text-success-70 text-xs font-bold uppercase tracking-wide">
                    <Star className="w-4 h-4" /> Their win
                  </div>
                  <p className="text-gray-70 leading-relaxed whitespace-pre-wrap">{selectedWin.win_text}</p>
                </div>
                {selectedWin.key_takeaway && (
                  <div className="mb-5">
                    <div className="text-xs font-bold uppercase tracking-wide text-gray-40 mb-2">Key takeaway</div>
                    <p className="text-sm text-gray-60 italic">{selectedWin.key_takeaway}</p>
                  </div>
                )}
                <div className="border-t border-gray-20 pt-5">
                  <label className="block text-sm font-semibold text-gray-70 mb-2">Review status</label>
                  <select
                    value={selectedWin.review_status}
                    onChange={(event) => updateWin({ review_status: event.target.value as TrainingWin['review_status'] })}
                    disabled={isSaving}
                    className="w-full px-3 py-2.5 border border-gray-20 rounded-xl text-sm bg-white mb-4"
                  >
                    <option value="new">New</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="featured">Featured</option>
                    <option value="archived">Archived</option>
                  </select>
                  <label className="block text-sm font-semibold text-gray-70 mb-2">Private admin note</label>
                  <textarea
                    defaultValue={selectedWin.admin_note || ''}
                    key={selectedWin.id + selectedWin.admin_note}
                    onBlur={(event) => {
                      const note = event.target.value.trim() || null;
                      if (note !== selectedWin.admin_note) updateWin({ admin_note: note });
                    }}
                    disabled={isSaving}
                    rows={4}
                    placeholder="Add a private note about this win..."
                    className="w-full px-3 py-2.5 border border-gray-20 rounded-xl text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-success-40"
                  />
                  {isSaving && <p className="text-xs text-gray-40 mt-2">Saving review...</p>}
                </div>
                <div className="mt-5 pt-4 border-t border-gray-20 text-xs text-gray-40">
                  Submitted {new Date(selectedWin.submitted_at).toLocaleString()}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
